export type Lang = 'en' | 'fa' | 'ar'

/**
 * All site copy lives here. Two top-level keys: `en` and `fa`. Same shape both
 * sides, enforced by the `T` type. Adding a new string: add to both languages.
 *
 * Conventions mirror Trend (lib/i18n.ts):
 *   - Keep nesting shallow — flat is easier to grep.
 *   - Error messages live under `contact.errors` so the Zod schema can pick
 *     them up by language.
 *   - Strings that include a brand product name (e.g. "Botulinum toxin") are
 *     kept in English in both sides so we don't drift on transliteration.
 */
export const translations = {
  en: {
    nav: {
      products: 'Products',
      why: 'Why MRN',
      markets: 'Markets',
      faq: 'FAQ',
      journal: 'Journal',
      contact: 'Contact',
      cta: 'Request consultation →',
      menu: 'Menu',
      menuClose: 'Close menu',
      account: 'Account',
      login: 'Sign in',
      cart: 'Cart',
    },
    shop: {
      title: 'Shop',
      backToShop: '← Back to shop',
      intro: 'Selected MRN products, approved by the Ministry of Health.',
      addToCart: 'Add to cart',
      added: 'Added to cart',
      viewDetails: 'View product',
      priceOnRequest: 'Price on request',
      qty: 'Qty',
      empty: 'Your cart is empty.',
      emptyCta: 'Browse the shop',
      subtotal: 'Subtotal',
      remove: 'Remove',
      continueShopping: 'Continue shopping',
      checkout: 'Place order',
      checkoutNotice:
        'Due to recent fluctuations in the USD rate, orders are finalized over the phone with a sales representative.',
      requestCallback: 'Request a callback',
      callbackSent: 'Your request has been recorded. A sales representative will contact you shortly.',
      orderError: 'Could not place the order. Please try again.',
      checkoutTitle: 'Complete your order',
      deliveryAddress: 'Delivery address',
      addAddressPrompt: 'Add an address to your account to continue.',
      addAddressCta: 'Add an address',
      deliveryMethod: 'Delivery method',
      deliveryCourier: 'Delivery by company courier',
      deliveryPost: 'Delivery by post',
      paymentGateway: 'Payment gateway',
      gatewayMellat: 'Bank Mellat gateway',
      gatewayZarinpal: 'ZarinPal gateway',
      gatewayParsian: 'Parsian gateway',
      orderSummary: 'Order summary',
      amountPayable: 'Amount payable',
      payOnline: 'Online payment',
      contactName: 'Full name',
      contactPhone: 'Phone number',
      contactEmail: 'Email (optional)',
      note: 'Notes (optional)',
      loginTitle: 'Sign in',
      signupTitle: 'Create an account',
      email: 'Email',
      password: 'Password',
      fullName: 'Full name',
      phone: 'Phone number',
      loginSubmit: 'Sign in',
      signupSubmit: 'Sign up',
      googleSignIn: 'Continue with Google',
      or: 'or',
      noAccount: "Don't have an account? Sign up",
      haveAccount: 'Already have an account? Sign in',
      loginError: 'Invalid email or password.',
      signupError: 'Sign-up failed.',
      googleError: 'Google sign-in failed.',
      verifyTitle: 'Verify your email',
      verifySub: 'Enter the 6-digit code we sent to:',
      codeLabel: 'Verification code',
      verifySubmit: 'Verify & sign in',
      verifyError: 'Incorrect or expired code. Please try again.',
      resendCode: 'Resend code',
      resendDone: 'A new code was sent.',
      resendWait: 'Please wait a moment and try again.',
      changeEmail: 'Back / edit email',
      accountTitle: 'My account',
      emailVerified: 'Email verified',
      emailNotVerified: 'Email not verified',
      verifyEmailCta: 'Verify email',
      verifyEmailSoon: 'Email verification will be available soon.',
      profile: 'Profile',
      save: 'Save',
      saved: 'Saved',
      myRequests: 'My requests',
      ordersTitle: 'Order history',
      ordersEmpty: 'No orders yet.',
      orderStatusNew: 'Awaiting callback',
      orderStatusHandled: 'Contacted',
      logoutCta: 'Sign out',
      // Active devices (session list)
      devicesTitle: 'Active devices',
      deviceCurrent: 'This device',
      deviceLastSeen: 'Last activity',
      deviceRevoke: 'Sign out',
      devicesLogoutAll: 'Sign out of other devices',
      // Buyer identity
      customerTypeLabel: 'Customer type',
      typeIndividual: 'Individual',
      typeCompany: 'Company / Organization',
      businessType: 'Activity',
      bizPharmacy: 'Pharmacy',
      bizClinic: 'Clinic',
      bizHospital: 'Hospital',
      bizDistributor: 'Distributor',
      bizOther: 'Other',
      businessTypeNone: 'Not specified',
      nationalId: 'National ID',
      companyName: 'Company / center name',
      companyNationalId: 'Company national ID',
      economicCode: 'Economic code',
      registrationNumber: 'Registration number',
      // Address book
      addressesTitle: 'Addresses',
      addressesEmpty: 'No saved addresses.',
      addAddress: 'Add address',
      editAddress: 'Edit address',
      addressLabel: 'Label (e.g. Office)',
      recipientName: 'Recipient',
      province: 'Province',
      city: 'City',
      addressLine: 'Full address',
      postalCode: 'Postal code',
      defaultBadge: 'Default',
      setDefault: 'Set as default',
      deleteCta: 'Delete',
      cancel: 'Cancel',
      // Orders detail
      orderLabel: 'Order',
      shipTo: 'Ship to',
      total: 'Total',
      payUnpaid: 'Unpaid',
      payPending: 'Payment pending',
      payPaid: 'Paid',
      payRefunded: 'Refunded',
      ostAwaitingCall: 'Awaiting callback',
      ostConfirmed: 'Confirmed',
      ostProcessing: 'Processing',
      ostShipped: 'Shipped',
      ostDelivered: 'Delivered',
      ostCancelled: 'Cancelled',
    },
    hero: {
      eyebrow: '',
      h1a: 'Mahrokh Naz',
      h1b: '',
      h1c: '',
      tagline: 'Pioneering the medical-supply chain in Iran.',
      sub: "MRN carries authenticated medical supplies from its manufacturers in Iran to the markets that rely on it across the Middle East, Caucasus, and Central Asia. From our Tehran hub, we ship Masport, Dyston, and Luxiva in temperature-monitored shippers — 48 hours to Yerevan, 72 to Baku.",
      signature: 'Cold-Chain Active | Tracked Logistics | Tehran → Regional Delivery',
      cta1: 'Request consultation',
      cta2: 'View the portfolio',
      scroll: 'Scroll',
    },
    products: {
      section: 'Portfolio',
      heading: 'Direct Manufacturer Sourcing. No Intermediaries.',
      sub: 'All brands are clinically validated with documented efficacy.',
      items: [
        {
          name: 'Masport',
          tagline: 'Botulinum toxin Type A',
          description:
            'Botulinum Toxin Type A (Manufactured by Masoun Darou)\n\nPresentation: 500-Unit single-dose vial, accompanied by sterile sodium chloride solvent.\n\nFormulation: Lyophilized powder for reconstitution and intramuscular injection.\n\nRegulatory Status: Approved by the Ministry of Health and Medical Education of Iran.',
          notes: ['500U / vial', 'Sterile solvent included', 'Cold chain 2–8°C'] as string[],
        },
        {
          name: 'Dyston',
          tagline: 'Botulinum toxin Type A (haemagglutinin complex)',
          description:
            'Botulinum Toxin Type A-Hemagglutinin Complex (Manufactured by Atra Zist Aray)\n\nConcentration: 500 Units per vial.\n\nPackaging: Standard pharmaceutical packaging.\n\nRegulatory Status: Approved by the Ministry of Health and Medical Education of Iran.',
          notes: ['500U / vial', 'Haemagglutinin complex', 'Cold chain 2–8°C'] as string[],
        },
        {
          name: 'Luxiva',
          tagline: 'Mesotherapy gel',
          description:
            'Non-Cross-Linked Hyaluronic Acid Meso-Gel (Manufactured by Atra Zist Aray)\n\nIndication: Designed to improve skin quality, enhance hydration, restore elasticity, and promote an even skin tone.\n\nProduct Status: Sterile and single-use.\n\nLogistics & Storage: Requires cold chain transportation (refrigerated transit).\n\nRegulatory Status: Approved by the Ministry of Health and Medical Education of Iran.',
          notes: ['Sterile single-use', 'No animal-source ingredients', 'Refrigerated transit'] as string[],
        },
      ],
      detailCta: 'Discuss this brand',
      viewProduct: 'View product',
    },
    why: {
      section: 'Why MRN',
      heading: 'The commitment behind every shipment.',
      sub: "We move these products through the region's distribution networks — the same chain of custody, the same name on every shipment.",
      pillars: [
        {
          title: 'Direct from manufacturer',
          body: "Every shipment originates at the manufacturer — no parallel imports, no grey-market repackaging. We hold primary distribution agreements for every brand we carry. If the paperwork doesn't trace to the production run, the shipment doesn't leave our warehouse.",
        },
        {
          title: 'Delivered in less than 72 hours',
          body: 'From our Tehran hub, the fastest routes in the region put your order in hand in less than 72 hours — not the weeks a European transit costs you. Every shipment still travels in a temperature-monitored shipper, so speed never breaks the cold chain.',
        },
        {
          title: 'No broken chain',
          body: "Every shipment leaves with a data logger sealed inside. If the chain breaks, it doesn't ship. No exceptions.",
        },
      ],
      chapters: [
        {
          kicker: 'Sourcing',
          headline: 'The same product can enter the market in various ways.',
          body: 'What you receive is directly dependent on the sourcing channel and the integrity of the supply chain.',
        },
        {
          kicker: '01 — The source',
          headline: 'First-Hand Supply, Not Forth-Hand Circulation',
          body: 'We are the primary distributor for Masport, Dyston, and Luxiva.\n\nEvery shipment is linked directly to its production batch — not re-export paperwork or layered trade chains.\n\nYou source directly from the distributor, not from a chain of intermediaries.',
        },
        {
          kicker: '02 — The stock',
          headline: 'Long shelf life, not warehouse-stored inventory.',
          body: 'Direct sourcing ensures products arrive with full remaining shelf life and optimal condition — not stock that has lost time moving through multi-layered intermediary channels before reaching you.',
        },
        {
          kicker: '03 — The route',
          headline: 'Distribution & Logistics Advantage',
          body: 'Direct flow from a Tehran hub into regional markets via streamlined supply routes, bypassing layered intermediaries and reducing time, cost, and handling.\n\nFaster delivery. Fresher stock. Lower landed cost.',
        },
        {
          kicker: 'The standard',
          headline: 'Cold-Chain Integrity, Fully Monitored.',
          body: 'Every shipment moves under controlled temperature conditions with end-to-end data logging from hub to destination.\n\nThis standard has been maintained consistently for years—without exception.',
        },
      ],
    },
    markets: {
      section: 'Regional Hub',
      heading: 'From Tehran across the region — in days, not weeks.',
      sub: 'Tehran’s strategic location enables faster access, shorter supply routes, and more efficient distribution across regional markets.\n\nLess time. Less risk. Greater supply-chain reliability.',
      origin: 'Tehran · home base',
      countersHeading: 'Active markets',
      arcsHeading: 'Cold-chain routes',
      activeMarket: 'Active distribution market',
      tapHint: 'Hover, tap, or focus a market for details.',
      mapLabel: 'Map of the region MRN supplies, with Tehran as the hub.',
      selectLabel: 'Select a market',
    },
    midCta: {
      eyebrow: 'Work with MRN',
      heading: 'Access a verified supply hub across regional markets.',
      sub: 'For inquiries and collaboration requests, please submit your request. We will get back to you as soon as possible.',
      button: 'Request a consultation',
    },
    faq: {
      section: 'Questions',
      heading: 'The questions we hear first.',
      sub: "If the answer you need isn't here, the contact form is a faster route than email.",
      items: [
        {
          q: 'Do I need to be a licensed practitioner to order?',
          a: 'Yes. MRN supplies licensed, authorised buyers only — clinics, pharmacies, and distributors. We verify your medical or pharmaceutical licence before opening an account.',
        },
        {
          q: 'Do you publish pricing?',
          a: "No. Pricing depends on the brand, the lot, volume commitments, and shipping destination. Open a conversation through the form and we'll send the current price sheet.",
        },
        {
          q: 'What are your minimum order quantities?',
          a: 'They vary from product to product. A representative will walk you through the minimums and help right-size your first order — reach out through the form to get started.',
        },
        {
          q: 'How do you maintain the cold chain?',
          a: 'Validated insulated shippers with phase-change packs and a data logger in every box. Domestic deliveries arrive same- or next-day; international moves by air freight only.',
        },
        {
          q: 'Can you supply outside Iran?',
          a: 'Yes. We handle cross-border orders across the region. Customs documentation and importer-of-record handling are part of the service.',
        },
        {
          q: 'What happens if a lot is short-dated or damaged?',
          a: "We replace it. Every shipment carries a written quality guarantee — if the visual inspection fails or the data logger flags a temperature excursion, you don't pay for it.",
        },
      ],
    },
    contact: {
      section: 'Contact',
      heading: 'Open a conversation',
      sub: 'MRN supplies authenticated medical supplies across the region. Leave your number, and our dedicated sales team will reach out to you directly.',
      callbackPhoneLabel: 'Phone number',
      callbackPhonePlaceholder: '+98 21 …',
      callbackSubmit: 'Have us call',
      callbackSubmitting: 'Sending…',
      callbackSuccessTitle: 'We have your number.',
      callbackSuccessSub: 'Our dedicated sales team will reach out within two business days.',
      callbackTiming: 'Our sales team responds within two business days, during regional business hours.',
      callbackPrivacy: 'Confidential. No automated calls, no mailing list — a dedicated sales specialist handles your enquiry from start to finish.',
      callbackExpand: 'Or fill out the full form',
      callbackCollapse: 'Hide the detailed form',
      callbackPhoneRequired: 'Please enter your phone number',
      callbackPhoneInvalid: 'That phone number looks too short',
      callbackAdminLabel: 'Callback request via website',
      name: 'Full name',
      company: 'Company or organisation',
      email: 'Work email',
      phone: 'Phone',
      country: 'Country',
      message: "Tell us what you're looking for…",
      attachmentLabel: 'Attach licence or brief (optional · PDF, DOC, JPG, PNG, WEBP · 10 MB max)',
      attachmentChange: 'Replace file',
      attachmentRemove: 'Remove',
      submit: 'Send',
      submitting: 'Sending…',
      successTitle: 'Message received',
      successSub: "We'll be in touch within one business day.",
      error: 'Something went wrong. Try again or email us directly.',
      directEmail: 'contact@mrnmed.com',
      address: 'Tangestan 4 St., Hayat Sabz Complex, Niavaran, Tehran, Iran',
      errors: {
        nameRequired: 'Please enter your name',
        nameTooLong: 'Name must be 100 characters or fewer',
        companyRequired: 'Please enter your company or organisation name',
        companyTooLong: 'Company name must be 200 characters or fewer',
        emailRequired: 'Please enter your work email',
        emailInvalid: 'That email address looks off — check the format',
        phoneTooLong: 'Phone number must be 40 characters or fewer',
        countryTooLong: 'Country must be 80 characters or fewer',
        messageTooLong: 'Message must be 5000 characters or fewer',
        attachmentTooLarge: 'File is larger than 10 MB',
        attachmentTypeInvalid: 'Allowed types: PDF, DOC, DOCX, JPG, PNG, WEBP',
      },
    },
    footer: {
      tagline: 'Authenticated medical supplies for licensed professionals.',
      rights: 'All rights reserved.',
      navHeading: 'Explore',
      contactHeading: 'Contact',
      disclaimer:
        'Products supplied for use by licensed healthcare professionals only. Not intended for self-administration.',
      backToTop: 'Back to top',
    },
  },

  fa: {
    nav: {
      products: 'محصولات',
      why: 'چرا MRN',
      markets: 'بازارها',
      faq: 'سوالات',
      journal: 'وبلاگ',
      contact: 'تماس',
      cta: 'درخواست مشاوره ←',
      menu: 'منو',
      menuClose: 'بستن منو',
      account: 'حساب من',
      login: 'ورود',
      cart: 'سبد خرید',
    },
    shop: {
      title: 'فروشگاه',
      backToShop: 'بازگشت به فروشگاه →',
      intro: 'محصولات منتخب MRN، با تأییدیه وزارت بهداشت ایران.',
      addToCart: 'افزودن به سبد',
      added: 'به سبد افزوده شد',
      viewDetails: 'مشاهده محصول',
      priceOnRequest: 'استعلام قیمت',
      qty: 'تعداد',
      empty: 'سبد خرید شما خالی است.',
      emptyCta: 'مشاهده فروشگاه',
      subtotal: 'جمع کل',
      remove: 'حذف',
      continueShopping: 'ادامه خرید',
      checkout: 'ثبت سفارش',
      checkoutNotice:
        'به‌دلیل نوسانات اخیر نرخ دلار، نهایی‌شدن سفارش از طریق تماس تلفنی با کارشناس فروش انجام می‌شود.',
      requestCallback: 'درخواست تماس کارشناس',
      callbackSent: 'درخواست شما ثبت شد. کارشناس فروش به‌زودی با شما تماس می‌گیرد.',
      orderError: 'ثبت سفارش ممکن نشد. لطفاً دوباره تلاش کنید.',
      checkoutTitle: 'تکمیل سفارش',
      deliveryAddress: 'آدرس تحویل',
      addAddressPrompt: 'برای ادامه، یک آدرس در حساب کاربری خود ثبت کنید.',
      addAddressCta: 'افزودن آدرس',
      deliveryMethod: 'روش ارسال',
      deliveryCourier: 'ارسال با پیک مجموعه',
      deliveryPost: 'ارسال با پست',
      paymentGateway: 'درگاه پرداخت',
      gatewayMellat: 'درگاه بانک ملت',
      gatewayZarinpal: 'درگاه زرین‌پال',
      gatewayParsian: 'درگاه پارسیان',
      orderSummary: 'خلاصه سفارش',
      amountPayable: 'مبلغ قابل پرداخت',
      payOnline: 'پرداخت اینترنتی',
      contactName: 'نام و نام خانوادگی',
      contactPhone: 'شماره تماس',
      contactEmail: 'ایمیل (اختیاری)',
      note: 'توضیحات (اختیاری)',
      loginTitle: 'ورود به حساب',
      signupTitle: 'ساخت حساب کاربری',
      email: 'ایمیل',
      password: 'گذرواژه',
      fullName: 'نام و نام خانوادگی',
      phone: 'شماره تماس',
      loginSubmit: 'ورود',
      signupSubmit: 'ثبت‌نام',
      googleSignIn: 'ورود با گوگل',
      or: 'یا',
      noAccount: 'حساب ندارید؟ ثبت‌نام کنید',
      haveAccount: 'حساب دارید؟ وارد شوید',
      loginError: 'ایمیل یا گذرواژه نادرست است.',
      signupError: 'ثبت‌نام ناموفق بود.',
      googleError: 'ورود با گوگل ناموفق بود.',
      verifyTitle: 'تأیید ایمیل',
      verifySub: 'کد ۶ رقمی ارسال‌شده به ایمیل زیر را وارد کنید:',
      codeLabel: 'کد تأیید',
      verifySubmit: 'تأیید و ورود',
      verifyError: 'کد نادرست است یا منقضی شده. دوباره تلاش کنید.',
      resendCode: 'ارسال دوباره کد',
      resendDone: 'کد جدید ارسال شد.',
      resendWait: 'کمی صبر کنید و دوباره تلاش کنید.',
      changeEmail: 'بازگشت و ویرایش ایمیل',
      accountTitle: 'حساب کاربری',
      emailVerified: 'ایمیل تأیید شده',
      emailNotVerified: 'ایمیل تأیید نشده',
      verifyEmailCta: 'تأیید ایمیل',
      verifyEmailSoon: 'امکان تأیید ایمیل به‌زودی فعال می‌شود.',
      profile: 'مشخصات',
      save: 'ذخیره',
      saved: 'ذخیره شد',
      myRequests: 'درخواست‌های من',
      ordersTitle: 'سوابق سفارش',
      ordersEmpty: 'هنوز سفارشی ثبت نکرده‌اید.',
      orderStatusNew: 'در انتظار تماس',
      orderStatusHandled: 'پیگیری شد',
      logoutCta: 'خروج از حساب',
      // دستگاه‌های فعال (فهرست نشست‌ها)
      devicesTitle: 'دستگاه‌های فعال',
      deviceCurrent: 'این دستگاه',
      deviceLastSeen: 'آخرین فعالیت',
      deviceRevoke: 'خروج',
      devicesLogoutAll: 'خروج از سایر دستگاه‌ها',
      // هویت خریدار
      customerTypeLabel: 'نوع مشتری',
      typeIndividual: 'حقیقی',
      typeCompany: 'حقوقی (شرکت/سازمان)',
      businessType: 'نوع فعالیت',
      bizPharmacy: 'داروخانه',
      bizClinic: 'درمانگاه',
      bizHospital: 'بیمارستان',
      bizDistributor: 'پخش/توزیع',
      bizOther: 'سایر',
      businessTypeNone: 'نامشخص',
      nationalId: 'کد ملی',
      companyName: 'نام شرکت / مرکز',
      companyNationalId: 'شناسه ملی',
      economicCode: 'کد اقتصادی',
      registrationNumber: 'شماره ثبت',
      // دفترچه آدرس
      addressesTitle: 'نشانی‌ها',
      addressesEmpty: 'نشانی ذخیره‌شده‌ای ندارید.',
      addAddress: 'افزودن نشانی',
      editAddress: 'ویرایش نشانی',
      addressLabel: 'برچسب (مثلاً دفتر مرکزی)',
      recipientName: 'تحویل‌گیرنده',
      province: 'استان',
      city: 'شهر',
      addressLine: 'نشانی کامل',
      postalCode: 'کد پستی',
      defaultBadge: 'پیش‌فرض',
      setDefault: 'تنظیم به‌عنوان پیش‌فرض',
      deleteCta: 'حذف',
      cancel: 'انصراف',
      // جزئیات سفارش
      orderLabel: 'سفارش',
      shipTo: 'ارسال به',
      total: 'مجموع',
      payUnpaid: 'پرداخت‌نشده',
      payPending: 'در انتظار پرداخت',
      payPaid: 'پرداخت‌شده',
      payRefunded: 'بازگشت وجه',
      ostAwaitingCall: 'در انتظار تماس',
      ostConfirmed: 'تأییدشده',
      ostProcessing: 'در حال آماده‌سازی',
      ostShipped: 'ارسال‌شده',
      ostDelivered: 'تحویل‌شده',
      ostCancelled: 'لغوشده',
    },
    hero: {
      eyebrow: '',
      h1a: 'مه رخ ناز',
      h1b: '',
      h1c: '',
      tagline: 'پیشگام در زنجیره تأمین تجهیزات پزشکی در ایران',
      sub: 'همراه مراکز درمانی، بیمارستان‌ها، کلینیک‌ها و داروخانه‌ها در تجهیزات پزشکی استاندارد و مورد تأیید.',
      signature: 'کنترل کیفیت • تحویل مطمئن • پشتیبانی',
      cta1: 'دریافت مشاوره',
      cta2: 'مشاهده کاتالوگ',
      scroll: 'پایین',
    },
    products: {
      section: 'پورتفولیو',
      heading: 'محصولات ما',
      sub: 'ارائه طیف متنوعی از تجهیزات و ملزومات پزشکی با کیفیت استاندارد برای مراکز درمانی، بیمارستان‌ها، کلینیک‌ها و داروخانه‌ها.',
      items: [
        {
          name: 'ماسک سه‌لایه پزشکی',
          tagline: 'پاوان طب · بسته ۵۰ عددی',
          description:
            'ماسک تنفسی پاوان طب، سه‌لایه پزشکی با لایه میانی ملت‌بلون و قابلیت بالای فیلتر هوا.\n\nوزن ۱۰۰ گرم · مناسب آزمایشگاه و بیمارستان · بسته ۵۰ عددی.\n\nقیمت: ۳۲۰٬۰۰۰ تومان',
          notes: ['سه‌لایه با فیلتر', 'آزمایشگاه و بیمارستان', 'بسته ۵۰ عددی'] as string[],
        },
        {
          name: 'گان یک‌بار مصرف جراحی',
          tagline: 'ساخت ایران',
          description:
            'گان یک‌بار مصرف جراحی از پارچه بافتینهٔ باکیفیت و ضد حساسیت، با دوخت مستحکم و نخ مرغوب و دور آستین کش‌بافت.\n\nوزن ۳۸ گرم · رنگ آبی · اندازه ۱۲۰ سانت · ساخت ایران · گارانتی اصالت و سلامت فیزیکی کالا.\n\nقیمت هر عدد: ۲۳٬۸۰۰ تومان',
          notes: ['ضد حساسیت', 'اندازه ۱۲۰ سانت', 'فروش تکی'] as string[],
        },
        {
          name: 'دستکش وینیل نئومکس',
          tagline: 'سایز M · بسته ۱۰۰ عددی',
          description:
            'دستکش وینیل بدون پودر نئومکس، مدل EXCELLENT FINISHING، ساخت حریر ایران.\n\nسایز مدیوم (M) · بسته ۱۰۰ عددی · کارتن ۱۰ بسته.\n\nقیمت هر بسته: ۶۲۰٬۰۰۰ تومان',
          notes: ['بدون پودر', 'سایز M', 'بسته ۱۰۰ عددی'] as string[],
        },
      ],
      detailCta: 'گفت‌وگو درباره این برند',
      viewProduct: 'مشاهده محصول',
    },
    why: {
      section: 'چرا MRN',
      heading: 'تعهد ما، پشت هر محموله.',
      sub: 'این فرآورده‌ها را در شبکه‌های توزیع کشور جابه‌جا می‌کنیم — همان زنجیره تأمین، همان نام روی هر محموله.',
      pillars: [
        {
          title: 'مستقیم از تولیدکننده',
          body: 'هر محموله‌ای که ارسال می‌کنیم مستقیماً از تولیدکننده تأمین می‌شود — بدون واردات موازی، بدون بسته‌بندی مجدد بازار خاکستری. برای هر برندی که عرضه می‌کنیم توافقنامه توزیع دست‌اول داریم. اگر مدارک به سری ساخت ردیابی نشود، محموله از انبار ما خارج نمی‌شود.',
        },
        {
          title: 'تحویل در کمتر از ۷۲ ساعت',
          body: 'از مرکز ما در تهران، سریع‌ترین مسیرهای منطقه سفارش شما را در کمتر از ۷۲ ساعت به دستتان می‌رسانند — نه هفته‌هایی که ترانزیت اروپایی هزینه‌اش را به شما تحمیل می‌کند. هر محموله همچنان در محفظه‌ی پایش‌دما حمل می‌شود، تا سرعت هرگز زنجیره سرد را نشکند.',
        },
        {
          title: 'بدون انقطاع زنجیره',
          body: 'هر محموله با یک پایشگر دمای پلمب‌شده در داخل آن ارسال می‌شود. اگر زنجیره قطع شود، محموله ارسال نمی‌شود. بدون استثنا.',
        },
      ],
      chapters: [
        {
          kicker: 'کیفیت',
          headline: 'کیفیت، پایه اعتماد است',
          body: 'در حوزه تجهیزات پزشکی، کیفیت و اطمینان از اصالت محصولات اهمیت بالایی دارد. ما با عرضه محصولات استاندارد و کنترل مستمر کیفیت، تلاش می‌کنیم راهکاری مطمئن و قابل اعتماد برای مراکز درمانی، بیمارستان‌ها، کلینیک‌ها و داروخانه‌ها فراهم کنیم.',
        },
        {
          kicker: 'منبع تأمین',
          headline: 'شفافیت در تأمین، قدرت در توزیع',
          body: 'ما به عنوان شرکت پخش تجهیزات پزشکی، محصولات را از مسیرهای رسمی و قابل ردیابی وارد چرخه توزیع می‌کنیم. تمرکز ما بر اصالت، شفافیت و دسترسی مطمئن است تا هر محصول با اطلاعات کامل و قابل اتکا به دست مراکز درمانی، کلینیک‌ها و داروخانه‌ها برسد.',
        },
        {
          kicker: 'موجودی',
          headline: 'موجودی پایدار و قابل اتکا در چرخه توزیع',
          body: 'در فرآیند پخش تجهیزات پزشکی، ما بر تأمین مستمر و مدیریت دقیق موجودی تمرکز داریم. هدف ما ایجاد دسترسی پایدار برای مراکز درمانی، کلینیک‌ها و داروخانه‌ها است تا جریان تأمین بدون وقفه و با اطمینان ادامه داشته باشد.',
        },
        {
          kicker: 'استاندارد',
          headline: 'کنترل کیفیت و پشتیبانی در کل چرخه توزیع',
          body: 'در فرآیند پخش تجهیزات پزشکی، تمامی مراحل بر اساس استانداردهای کنترل کیفیت و نظارت مستمر انجام می‌شود. ما با تمرکز بر پشتیبانی پایدار و پایش دقیق فرآیند توزیع، اطمینان می‌دهیم محصولات با کیفیت ثابت و قابل اتکا به مراکز درمانی، کلینیک‌ها و داروخانه‌ها برسد.',
        },
      ],
    },
    markets: {
      section: 'هاب منطقه‌ای',
      heading: 'از تهران به سراسر منطقه؛ در روزها، نه هفته‌ها.',
      sub: 'موقعیت راهبردی تهران امکان دسترسی سریع‌تر، مسیرهای کوتاه‌تر و توزیع کارآمدتر را در بازارهای منطقه فراهم می‌کند.\n\nزمان کمتر. ریسک کمتر. زنجیره تأمین مطمئن‌تر.',
      origin: 'تهران · پایگاه اصلی',
      countersHeading: 'بازارهای فعال',
      arcsHeading: 'مسیرهای زنجیره سرد',
      activeMarket: 'بازار توزیع فعال',
      tapHint: 'برای جزئیات، نشانگر را روی بازار ببرید، لمس کنید یا فوکوس کنید.',
      mapLabel: 'نقشهٔ منطقهٔ تحت پوشش MRN با مرکزیت تهران.',
      selectLabel: 'انتخاب بازار',
    },
    midCta: {
      eyebrow: 'همکاری با MRN',
      heading: 'دسترسی به شبکه پخش تجهیزات پزشکی',
      sub: 'ما امکان همکاری با مراکز درمانی، کلینیک‌ها و فعالان حوزه سلامت را فراهم کرده‌ایم تا از طریق شبکه توزیع MRN به محصولات معتبر و جریان تأمین پایدار دسترسی داشته باشند.\n\nبرای دریافت اطلاعات و آغاز همکاری، درخواست خود را ثبت کنید. تیم ما در کوتاه‌ترین زمان پاسخ‌گو خواهند بود.',
      button: 'درخواست مشاوره',
    },
    faq: {
      section: 'سوالات',
      heading: 'نخستین پرسش‌هایی که می‌شنویم.',
      sub: 'اگر پاسخ موردنیازتان اینجا نیست، فرم تماس سریع‌تر از ایمیل جواب می‌گیرد.',
      items: [
        {
          q: 'برای سفارش حتماً باید دارای مجوز پزشکی باشم؟',
          a: 'بله. MRN تنها به خریداران دارای مجوز و پروانه‌ی رسمی عرضه می‌کند. پیش از باز کردن هر حساب، مجوز پزشکی یا دارویی تأیید می‌شود.',
        },
        {
          q: 'قیمت‌ها را عمومی منتشر می‌کنید؟',
          a: 'خیر. قیمت بستگی به برند، لات، تعهد حجم و مقصد ارسال دارد. از طریق فرم تماس بگیرید، لیست قیمت روز را ارسال می‌کنیم.',
        },
        {
          q: 'حداقل سفارش چقدر است؟',
          a: 'بسته به محصول متفاوت است. یک نماینده حداقل‌ها را برایتان توضیح می‌دهد و در تنظیم اندازه سفارش اول کمک می‌کند — برای شروع از طریق فرم تماس بگیرید.',
        },
        {
          q: 'زنجیره سرد چطور حفظ می‌شود؟',
          a: 'بسته‌بندی عایق با پک‌های تغییر فاز و دیتالاگر در هر کارتن. ارسال داخلی همان‌روز یا روز بعد؛ بین‌المللی فقط هوایی.',
        },
        {
          q: 'بیرون از ایران هم ارسال می‌کنید؟',
          a: 'بله. سفارش‌های فرامرزی را در سراسر منطقه پشتیبانی می‌کنیم. اسناد گمرکی و مدیریت واردکننده‌ی ثبت‌شده بخشی از خدمات است.',
        },
        {
          q: 'اگر لاتی نزدیک به انقضا یا آسیب‌دیده باشد چه می‌شود؟',
          a: 'تعویض می‌کنیم. هر محموله با ضمانت کیفیت کتبی ارسال می‌شود — اگر بازرسی ظاهری رد شود یا دیتالاگر هشدار دما بزند، هزینه‌اش با شما نیست.',
        },
      ],
    },
    contact: {
      section: 'تماس',
      heading: 'شروع گفت‌وگو',
      sub: 'مجموعه MRN به عنوان فعال در پخش تجهیزات پزشکی، امکان ارتباط مستقیم برای مراکز درمانی، کلینیک‌ها و فعالان حوزه سلامت را فراهم کرده است. شماره تماس خود را ثبت کنید تا کارشناسان فروش در اولین فرصت با شما تماس بگیرند.',
      callbackPhoneLabel: 'شماره تماس',
      callbackPhonePlaceholder: '+۹۸ ۲۱ …',
      callbackSubmit: 'درخواست تماس',
      callbackSubmitting: 'در حال ارسال…',
      callbackSuccessTitle: 'شماره شما دریافت شد.',
      callbackSuccessSub: 'کارشناسان فروش ما در دو روز کاری با شما تماس خواهند گرفت.',
      callbackTiming: 'پاسخ‌گویی در ساعات اداری و حداکثر طی دو روز کاری انجام می‌شود.',
      callbackPrivacy: 'محرمانه. بدون تماس خودکار، بدون فهرست تبلیغاتی — یک کارشناس فروش اختصاصی از ابتدا تا انتها پیگیر درخواست شماست.',
      callbackExpand: 'یا فرم کامل را پر کنید',
      callbackCollapse: 'پنهان کردن فرم تفصیلی',
      callbackPhoneRequired: 'شماره تماس خود را وارد کنید',
      callbackPhoneInvalid: 'شماره تماس کوتاه به نظر می‌رسد',
      callbackAdminLabel: 'درخواست تماس از طریق وب‌سایت',
      name: 'نام و نام خانوادگی',
      company: 'نام مرکز',
      email: 'ایمیل کاری',
      phone: 'شماره تماس',
      country: 'شهر',
      message: 'زمینه همکاری یا نیاز شما',
      attachmentLabel: 'پیوست مجوز یا الحاق فایل (اختیاری · PDF، DOC، JPG، PNG، WEBP · حداکثر ۱۰ مگابایت)',
      attachmentChange: 'تغییر فایل',
      attachmentRemove: 'حذف',
      submit: 'ارسال',
      submitting: 'در حال ارسال…',
      successTitle: 'پیام دریافت شد',
      successSub: 'در یک روز کاری با شما تماس می‌گیریم.',
      error: 'مشکلی پیش آمد. دوباره امتحان کنید یا مستقیماً ایمیل بزنید.',
      directEmail: 'contact@mrnmed.com',
      address: 'تهران، نیاوران، خیابان تنگستان ۴، مجتمع اداری تجاری حیات سبز',
      errors: {
        nameRequired: 'نام خود را وارد کنید',
        nameTooLong: 'نام باید حداکثر ۱۰۰ کاراکتر باشد',
        companyRequired: 'نام مرکز را وارد کنید',
        companyTooLong: 'نام مرکز باید حداکثر ۲۰۰ کاراکتر باشد',
        emailRequired: 'ایمیل کاری خود را وارد کنید',
        emailInvalid: 'فرمت ایمیل صحیح نیست',
        phoneTooLong: 'شماره تلفن باید حداکثر ۴۰ کاراکتر باشد',
        countryTooLong: 'نام شهر باید حداکثر ۸۰ کاراکتر باشد',
        messageTooLong: 'پیام باید حداکثر ۵۰۰۰ کاراکتر باشد',
        attachmentTooLarge: 'حجم فایل بیشتر از ۱۰ مگابایت است',
        attachmentTypeInvalid: 'فرمت‌های مجاز: PDF، DOC، DOCX، JPG، PNG، WEBP',
      },
    },
    footer: {
      tagline: 'تأمین تخصصی محصولات مصرفی پزشکی و حوزه سلامت',
      rights: 'تمامی حقوق محفوظ است.',
      navHeading: 'پیمایش',
      contactHeading: 'تماس با ما',
      disclaimer: 'محصولات تنها برای استفاده متخصصان دارای مجوز عرضه می‌شوند. مصرف شخصی ممنوع.',
      backToTop: 'بازگشت به بالا',
    },
  },

  ar: {
    nav: {
      products: 'المنتجات',
      why: 'لماذا MRN',
      markets: 'الأسواق',
      faq: 'الأسئلة',
      journal: 'المدوّنة',
      contact: 'تواصل',
      cta: 'اطلب استشارة ←',
      menu: 'القائمة',
      menuClose: 'إغلاق القائمة',
      account: 'حسابي',
      login: 'تسجيل الدخول',
      cart: 'السلة',
    },
    shop: {
      title: 'المتجر',
      backToShop: 'العودة إلى المتجر ←',
      intro: 'منتجات MRN المختارة، معتمدة من وزارة الصحة.',
      addToCart: 'أضف إلى السلة',
      added: 'أُضيف إلى السلة',
      viewDetails: 'عرض المنتج',
      priceOnRequest: 'السعر عند الطلب',
      qty: 'الكمية',
      empty: 'سلتك فارغة.',
      emptyCta: 'تصفّح المتجر',
      subtotal: 'الإجمالي',
      remove: 'إزالة',
      continueShopping: 'متابعة التسوق',
      checkout: 'إتمام الطلب',
      checkoutNotice:
        'نظراً للتقلبات الأخيرة في سعر الدولار، يتم إتمام الطلبات هاتفياً مع مندوب المبيعات.',
      requestCallback: 'اطلب معاودة الاتصال',
      callbackSent: 'تم تسجيل طلبك. سيتواصل معك مندوب المبيعات قريباً.',
      orderError: 'تعذّر تسجيل الطلب. يرجى المحاولة مرة أخرى.',
      checkoutTitle: 'إتمام الطلب',
      deliveryAddress: 'عنوان التسليم',
      addAddressPrompt: 'أضف عنواناً إلى حسابك للمتابعة.',
      addAddressCta: 'إضافة عنوان',
      deliveryMethod: 'طريقة التوصيل',
      deliveryCourier: 'التوصيل بمندوب المجموعة',
      deliveryPost: 'التوصيل بالبريد',
      paymentGateway: 'بوابة الدفع',
      gatewayMellat: 'بوابة بنك ملت',
      gatewayZarinpal: 'بوابة زرين‌بال',
      gatewayParsian: 'بوابة بارسيان',
      orderSummary: 'ملخص الطلب',
      amountPayable: 'المبلغ المستحق',
      payOnline: 'الدفع الإلكتروني',
      contactName: 'الاسم الكامل',
      contactPhone: 'رقم الهاتف',
      contactEmail: 'البريد الإلكتروني (اختياري)',
      note: 'ملاحظات (اختياري)',
      loginTitle: 'تسجيل الدخول',
      signupTitle: 'إنشاء حساب',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      fullName: 'الاسم الكامل',
      phone: 'رقم الهاتف',
      loginSubmit: 'دخول',
      signupSubmit: 'تسجيل',
      googleSignIn: 'المتابعة عبر جوجل',
      or: 'أو',
      noAccount: 'ليس لديك حساب؟ سجّل',
      haveAccount: 'لديك حساب؟ سجّل الدخول',
      loginError: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
      signupError: 'فشل التسجيل.',
      googleError: 'فشل تسجيل الدخول عبر جوجل.',
      verifyTitle: 'تأكيد البريد الإلكتروني',
      verifySub: 'أدخل الرمز المكوّن من 6 أرقام المُرسَل إلى:',
      codeLabel: 'رمز التأكيد',
      verifySubmit: 'تأكيد ودخول',
      verifyError: 'الرمز غير صحيح أو منتهي الصلاحية. حاول مرة أخرى.',
      resendCode: 'إعادة إرسال الرمز',
      resendDone: 'تم إرسال رمز جديد.',
      resendWait: 'يرجى الانتظار لحظة ثم المحاولة مرة أخرى.',
      changeEmail: 'رجوع / تعديل البريد',
      accountTitle: 'حسابي',
      emailVerified: 'البريد مُوثَّق',
      emailNotVerified: 'البريد غير مُوثَّق',
      verifyEmailCta: 'توثيق البريد',
      verifyEmailSoon: 'سيتوفّر توثيق البريد قريباً.',
      profile: 'الملف الشخصي',
      save: 'حفظ',
      saved: 'تم الحفظ',
      myRequests: 'طلباتي',
      ordersTitle: 'سجل الطلبات',
      ordersEmpty: 'لا توجد طلبات بعد.',
      orderStatusNew: 'بانتظار الاتصال',
      orderStatusHandled: 'تمت المتابعة',
      logoutCta: 'تسجيل الخروج',
      // الأجهزة النشطة (قائمة الجلسات)
      devicesTitle: 'الأجهزة النشطة',
      deviceCurrent: 'هذا الجهاز',
      deviceLastSeen: 'آخر نشاط',
      deviceRevoke: 'تسجيل الخروج',
      devicesLogoutAll: 'الخروج من الأجهزة الأخرى',
      // هوية المشتري
      customerTypeLabel: 'نوع العميل',
      typeIndividual: 'فرد',
      typeCompany: 'شركة / مؤسسة',
      businessType: 'النشاط',
      bizPharmacy: 'صيدلية',
      bizClinic: 'عيادة',
      bizHospital: 'مستشفى',
      bizDistributor: 'موزّع',
      bizOther: 'أخرى',
      businessTypeNone: 'غير محدد',
      nationalId: 'الرقم الوطني',
      companyName: 'اسم الشركة / المركز',
      companyNationalId: 'المعرّف الوطني للشركة',
      economicCode: 'الرمز الاقتصادي',
      registrationNumber: 'رقم التسجيل',
      // دفتر العناوين
      addressesTitle: 'العناوين',
      addressesEmpty: 'لا توجد عناوين محفوظة.',
      addAddress: 'إضافة عنوان',
      editAddress: 'تعديل العنوان',
      addressLabel: 'تسمية (مثل المكتب)',
      recipientName: 'المستلم',
      province: 'المحافظة',
      city: 'المدينة',
      addressLine: 'العنوان الكامل',
      postalCode: 'الرمز البريدي',
      defaultBadge: 'افتراضي',
      setDefault: 'تعيين كافتراضي',
      deleteCta: 'حذف',
      cancel: 'إلغاء',
      // تفاصيل الطلب
      orderLabel: 'طلب',
      shipTo: 'الشحن إلى',
      total: 'الإجمالي',
      payUnpaid: 'غير مدفوع',
      payPending: 'بانتظار الدفع',
      payPaid: 'مدفوع',
      payRefunded: 'مُسترد',
      ostAwaitingCall: 'بانتظار الاتصال',
      ostConfirmed: 'مؤكَّد',
      ostProcessing: 'قيد التجهيز',
      ostShipped: 'تم الشحن',
      ostDelivered: 'تم التسليم',
      ostCancelled: 'ملغى',
    },
    hero: {
      eyebrow: '',
      h1a: 'مه رخ ناز',
      h1b: '',
      h1c: '',
      tagline: 'رائدة في سلسلة تأمين التجهيزات الطبية في إيران.',
      sub: 'تنقل MRN التجهيزات الطبية الموثَّقة من مُصنّعيها في إيران إلى أسواق الشرق الأوسط والقوقاز وآسيا الوسطى. ومن مركزنا اللوجستي في طهران، نشحن مصبورت وديستون ولوكسيفا في حاويات مزوّدة بمراقِب حرارة — ٤٨ ساعة إلى يريفان و٧٢ إلى باكو.',
      signature: 'من طهران. عبر المنطقة. سلسلة تبريد بلا انقطاع.',
      cta1: 'اطلب استشارة',
      cta2: 'تصفّح المجموعة',
      scroll: 'مرّر',
    },
    products: {
      section: 'المجموعة',
      heading: 'ما نوزّعه — مباشرةً من المُصنّع.',
      sub: 'كل علامة نوزّعها تكتسب مكانها بالأدلّة السريرية، وتُنقل وفق معيار سلسلة التبريد نفسه.',
      items: [
        {
          name: 'مصبورت',
          tagline: 'توكسين البوتولينوم نوع A',
          description:
            'توكسين البوتولينوم نوع A من إنتاج مصون دارو. قارورة أحادية الاستخدام بسعة ٥٠٠ وحدة مع مُذيب كلوريد الصوديوم المعقَّم؛ مسحوق مُجفَّد بالتجميد للتحضير والحقن العضلي.',
          notes: ['٥٠٠ وحدة/قارورة', 'مُذيب معقَّم مُرفق', 'سلسلة تبريد ٢–٨°م'] as string[],
        },
        {
          name: 'ديستون',
          tagline: 'توكسين البوتولينوم نوع A (مركّب الهيماجلوتينين)',
          description:
            'تركيبة مركّب الهيماجلوتينين من توكسين البوتولينوم نوع A بتركيز ٥٠٠ وحدة في القارورة، من إنتاج أترا زيست آراي. تغليف دوائي قياسي مع ملصق فارسي ثانوي وفق الإطار التنظيمي الإيراني.',
          notes: ['٥٠٠ وحدة/قارورة', 'مركّب الهيماجلوتينين', 'سلسلة تبريد ٢–٨°م'] as string[],
        },
        {
          name: 'لوكسيفا',
          tagline: 'جل ميزوثيرابي',
          description:
            'ميزوجل قائم على حمض الهيالورونيك غير المتشابك، من إنتاج أترا زيست آراي، لتحسين جودة البشرة — الترطيب والمرونة وتوحيد اللون. معقَّم، أحادي الاستخدام، نقل مبرَّد.',
          notes: ['معقَّم أحادي الاستخدام', 'خالٍ من المكوّنات الحيوانية', 'نقل مبرَّد'] as string[],
        },
      ],
      detailCta: 'تحدّث عن هذه العلامة',
      viewProduct: 'عرض المنتج',
    },
    why: {
      section: 'لماذا MRN',
      heading: 'التزامنا خلف كل شحنة.',
      sub: 'ننقل هذه المنتجات عبر شبكات التوزيع في المنطقة — سلسلة العهدة نفسها، والاسم نفسه على كل شحنة.',
      pillars: [
        {
          title: 'مباشرةً من المُصنّع',
          body: 'كل شحنة نشحنها تنشأ لدى المُصنّع — بلا استيراد موازٍ، وبلا إعادة تغليف من السوق الرمادية. نملك اتفاقيات توزيع أولية لكل علامة نوزّعها. وإن لم تتتبّع الوثائق إلى دفعة الإنتاج، لا تغادر الشحنة مستودعنا.',
        },
        {
          title: 'التسليم في أقل من ٧٢ ساعة',
          body: 'من مركزنا في طهران، تضع أسرع مسارات المنطقة طلبك بين يديك في أقل من ٧٢ ساعة — لا الأسابيع التي يكلّفك إيّاها العبور الأوروبي. وتظلّ كل شحنة تُنقل في حاوية مراقَبة الحرارة، كي لا تكسر السرعةُ سلسلةَ التبريد أبداً.',
        },
        {
          title: 'بلا انقطاع للسلسلة',
          body: 'تغادر كل شحنة ومعها مُسجِّل بيانات حرارة مختوم بداخلها. وإن انقطعت السلسلة، لا تُشحن. بلا استثناء.',
        },
      ],
      chapters: [
        {
          kicker: 'التوريد',
          headline: 'الأصالة تبدأ من المصدر؛',
          body: 'في سوق التجهيزات الطبية، يمكن للمنتج أن يصل إليك عبر مسارات متعددة، ولكن قيمة هذه المسارات ليست متساوية أبداً. نحن في MRN، لا نوفر لك مجرد منتج، بل نضمن لك أمان مصدر التوريد؛ مباشرةً، ومراقباً، وبدون وسيط من خط الإنتاج.',
        },
        {
          kicker: '٠١ — المصدر',
          headline: 'توريد مباشر من المصدر.. لا تداول عبر الوساطات.',
          body: 'نحن الموزع الرئيسي لمنتجات Masport و Dyston و Luxiva.\n\nكل شحنة لدينا ترتبط مباشرةً بـ "دفعة الإنتاج" (Batch) الأصلية؛ بعيداً عن تعقيدات إعادة التصدير أو سلاسل التجارة المتعددة والغامضة.\n\nأنت تحصل على احتياجاتك من الموزع الأول مباشرةً، وليس عبر سلسلة من الوسطاء.',
        },
        {
          kicker: '٠٢ — المخزون',
          headline: 'صلاحية ممتدة.. لا مخزون راكد في المستودعات.',
          body: 'يضمن لك التوريد المباشر من المصدر وصول المنتجات بأقصى فترة صلاحية ممكنة وفي حالة مثالية؛ بعيداً عن البضائع المخزنة التي فقدت جزءاً من عمرها الافتراضي عبر قنوات الوساطة المتعددة قبل أن تصل إليك.',
        },
        {
          kicker: '٠٣ — المسار',
          headline: 'التفوق اللوجستي وشبكة التوزيع',
          body: 'تدفق مباشر من مركزنا اللوجستي في طهران إلى الأسواق الإقليمية عبر مسارات توريد انسيابية. نحن نتجاوز طبقات الوسطاء المتعددة لتقليل الوقت، التكلفة، ومخاطر النقل والمناولة المتكررة.\n\nتوصيل أسرع • مخزون أحدث • تكلفة وصول (Landed Cost) أقل',
        },
        {
          kicker: 'المعيار',
          headline: 'سلامة سلسلة التبريد.. تحت مراقبة شاملة.',
          body: 'تتحرك كل شحنة تحت ظروف حرارة خاضعة للرقابة الصارمة، مع تسجيل وتتبع مستمر للبيانات (Data Logging) من المركز اللوجستي إلى الوجهة النهائية.\n\nلقد حافظنا على هذا المعيار الصارم لسنوات طويلة — وبلا أي استثناء.',
        },
      ],
    },
    markets: {
      section: 'المركز الإقليمي',
      heading: 'من طهران إلى أنحاء المنطقة — خلال أيام لا أسابيع.',
      sub: 'يتيح الموقع الاستراتيجي لطهران وصولاً أسرع ومسارات توريد أقصر وتوزيعاً أكثر كفاءة عبر الأسواق الإقليمية.\n\nوقت أقل. مخاطر أقل. موثوقية أعلى لسلسلة الإمداد.',
      origin: 'طهران · المقرّ الرئيسي',
      countersHeading: 'الأسواق النشطة',
      arcsHeading: 'مسارات سلسلة التبريد',
      activeMarket: 'سوق توزيع نشط',
      tapHint: 'مرّر المؤشّر فوق سوق أو انقر عليه أو ركّز عليه لعرض التفاصيل.',
      mapLabel: 'خريطة المنطقة التي تغطّيها MRN مع طهران كمركز.',
      selectLabel: 'اختر سوقًا',
    },
    midCta: {
      eyebrow: 'التعاون مع MRN',
      heading: 'الوصول إلى مركز توريد معتمد في الأسواق الإقليمية.',
      sub: 'للاستفسارات وطلبات التعاون، يرجى إرسال طلبكم. سنقوم بالرد في أقرب وقت ممكن',
      button: 'اطلب استشارة',
    },
    faq: {
      section: 'الأسئلة',
      heading: 'أوّل الأسئلة التي نسمعها.',
      sub: 'إن لم تجد ما تبحث عنه هنا، فنموذج التواصل أسرع من البريد الإلكتروني.',
      items: [
        {
          q: 'هل يلزم أن أكون مرخَّصاً لأطلب؟',
          a: 'نعم. تورّد MRN للمشترين المرخَّصين والمعتمدين فقط. نتحقّق من الترخيص الطبي أو الدوائي قبل فتح أي حساب.',
        },
        {
          q: 'هل تنشرون الأسعار؟',
          a: 'لا. يعتمد السعر على العلامة والدفعة والتزامات الكمية ووجهة الشحن. تواصل عبر النموذج ونرسل لك قائمة الأسعار الحالية.',
        },
        {
          q: 'ما الحدّ الأدنى للطلب؟',
          a: 'يختلف من منتج إلى آخر. سيوضّح لك أحد ممثّلينا الحدود الدنيا ويساعدك في ضبط حجم طلبك الأول — تواصل عبر النموذج للبدء.',
        },
        {
          q: 'كيف تُحافظون على سلسلة التبريد؟',
          a: 'حاويات عازلة مُعتمدة مع عبوات تغيير الطور، ومُسجِّل بيانات في كل صندوق. التوصيل المحلي في اليوم نفسه أو التالي؛ والدولي شحناً جوياً فقط.',
        },
        {
          q: 'هل تورّدون خارج إيران؟',
          a: 'نعم. ندعم الطلبات العابرة للحدود في المنطقة بأكملها. وتشمل الخدمة المستندات الجمركية وإدارة المستورد المسجَّل.',
        },
        {
          q: 'ماذا لو كانت الدفعة قريبة من انتهاء الصلاحية أو تالفة؟',
          a: 'نستبدلها. تُشحن كل شحنة بضمان جودة مكتوب — فإن أخفق الفحص الظاهري أو سجّل المُسجِّل تجاوزاً حرارياً، فلا تدفع ثمنها.',
        },
      ],
    },
    contact: {
      section: 'تواصل',
      heading: 'لنبدأ الحديث',
      sub: 'توفر MRN تجهيزات طبية أصلية ومضمونة في جميع أنحاء المنطقة. اترك رقمك، وسيتواصل معك فريق المبيعات المختص لدينا مباشرةً.',
      callbackPhoneLabel: 'رقم الهاتف',
      callbackPhonePlaceholder: '+٩٨ ٢١ …',
      callbackSubmit: 'اتّصلوا بي',
      callbackSubmitting: 'جارٍ الإرسال…',
      callbackSuccessTitle: 'استلمنا رقمك.',
      callbackSuccessSub: 'سيتواصل معك فريق المبيعات المختص لدينا خلال يومَي عمل.',
      callbackTiming: 'يردّ فريق المبيعات المختص لدينا خلال يومَي عمل، ضمن ساعات العمل في المنطقة.',
      callbackPrivacy: 'سرّي. بلا اتصالات آلية، وبلا قوائم بريدية — يتولّى أخصائي مبيعات مختص طلبك من البداية إلى النهاية.',
      callbackExpand: 'أو املأ النموذج الكامل',
      callbackCollapse: 'إخفاء النموذج المفصّل',
      callbackPhoneRequired: 'الرجاء إدخال رقم هاتفك',
      callbackPhoneInvalid: 'يبدو رقم الهاتف قصيراً',
      callbackAdminLabel: 'طلب اتصال عبر الموقع',
      name: 'الاسم الكامل',
      company: 'الشركة أو المؤسسة',
      email: 'البريد الإلكتروني للعمل',
      phone: 'الهاتف',
      country: 'الدولة',
      message: 'أخبِرنا بما تبحث عنه…',
      attachmentLabel: 'إرفاق ترخيص أو موجز (اختياري · PDF، DOC، JPG، PNG، WEBP · ١٠ ميغابايت كحدّ أقصى)',
      attachmentChange: 'تغيير الملف',
      attachmentRemove: 'إزالة',
      submit: 'إرسال',
      submitting: 'جارٍ الإرسال…',
      successTitle: 'تمّ استلام الرسالة',
      successSub: 'سنتواصل معك خلال يوم عمل واحد.',
      error: 'حدث خطأ ما. حاول مجدداً أو راسِلنا مباشرة.',
      directEmail: 'contact@mrnmed.com',
      address: 'إيران، طهران، نياوران، شارع تنغستان ٤، مجمّع حياة سبز',
      errors: {
        nameRequired: 'الرجاء إدخال اسمك',
        nameTooLong: 'يجب ألا يتجاوز الاسم ١٠٠ حرف',
        companyRequired: 'الرجاء إدخال اسم شركتك أو مؤسستك',
        companyTooLong: 'يجب ألا يتجاوز اسم الشركة ٢٠٠ حرف',
        emailRequired: 'الرجاء إدخال بريدك الإلكتروني للعمل',
        emailInvalid: 'صيغة البريد الإلكتروني غير صحيحة',
        phoneTooLong: 'يجب ألا يتجاوز رقم الهاتف ٤٠ حرفاً',
        countryTooLong: 'يجب ألا يتجاوز اسم الدولة ٨٠ حرفاً',
        messageTooLong: 'يجب ألا تتجاوز الرسالة ٥٠٠٠ حرف',
        attachmentTooLarge: 'حجم الملف أكبر من ١٠ ميغابايت',
        attachmentTypeInvalid: 'الصيغ المسموحة: PDF، DOC، DOCX، JPG، PNG، WEBP',
      },
    },
    footer: {
      tagline: 'التجهيزات الطبية الموثَّقة، للمتخصّصين المرخَّصين.',
      rights: 'جميع الحقوق محفوظة.',
      navHeading: 'استكشف',
      contactHeading: 'تواصل',
      disclaimer: 'تُورَّد المنتجات لاستخدام المتخصّصين الصحّيين المرخَّصين فقط. ليست للاستخدام الذاتي.',
      backToTop: 'العودة إلى الأعلى',
    },
  },
} as const

export type T = typeof translations.en
